<script setup>
import { computed } from 'vue'
import { ChevronRight, Home } from 'lucide-vue-next'

const props = defineProps({
  items: {
    type: Array,
    required: true
    // Expected format: [{ label: 'Label', href?: string, to?: string|object, active?: boolean, onClick?: function, icon?: component }]
  },
  separator: {
    type: String,
    default: 'chevron',
    validator: (value) => ['chevron', 'slash', 'dash'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  showHome: {
    type: Boolean,
    default: true
  },
  homeHref: {
    type: String,
    default: '/'
  },
  homeTo: {
    type: [String, Object],
    default: null
  },
  background: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['click', 'home-click'])

const handleItemClick = (item, index) => {
  if (item.onClick) {
    item.onClick()
  }
  emit('click', item, index)
}

const handleHomeClick = () => {
  emit('home-click')
}

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'text-xs px-3 py-1.5',
    md: 'text-sm px-4 py-2',
    lg: 'text-base px-4 py-3'
  }
  return sizes[props.size]
})

const separatorIcon = computed(() => {
  return props.separator === 'chevron' ? ChevronRight : null
})

const separatorText = computed(() => {
  const separators = {
    slash: '/',
    dash: '—'
  }
  return separators[props.separator]
})

const containerClasses = computed(() => {
  return [
    'breadcrumb-nav',
    props.background ? 'bg-gray-50 border-b border-gray-200' : '',
    sizeClasses.value
  ].filter(Boolean).join(' ')
})

const iconSize = computed(() => {
  const sizes = {
    sm: 14,
    md: 16,
    lg: 18
  }
  return sizes[props.size]
})
</script>

<template>
  <nav :class="containerClasses" aria-label="Breadcrumb">
    <ol class="flex items-center space-x-2">
      <!-- Home item -->
      <li v-if="showHome">
        <component
          :is="homeTo ? 'router-link' : 'a'"
          :to="homeTo"
          :href="!homeTo ? homeHref : undefined"
          class="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-muted rounded-sm"
          @click="handleHomeClick"
          aria-label="Home"
        >
          <Home :size="iconSize" />
        </component>
      </li>
      
      <!-- Breadcrumb items -->
      <template v-for="(item, index) in items" :key="index">
        <!-- Separator -->
        <li 
          v-if="showHome || index > 0"
          class="flex items-center"
          aria-hidden="true"
        >
          <component
            v-if="separatorIcon"
            :is="separatorIcon"
            :size="iconSize"
            class="text-gray-400 mx-1"
          />
          <span
            v-else
            class="text-gray-400 mx-2"
          >
            {{ separatorText }}
          </span>
        </li>
        
        <!-- Breadcrumb item -->
        <li class="flex items-center">
          <component
            v-if="item.href || item.to"
            :is="item.to ? 'router-link' : 'a'"
            :to="item.to"
            :href="!item.to ? item.href : undefined"
            :class="[
              'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-muted rounded-sm',
              item.active 
                ? 'text-gray-900 font-medium cursor-default' 
                : 'text-gray-600 hover:text-gray-800 font-medium'
            ]"
            @click="!item.active && handleItemClick(item, index)"
            :aria-current="item.active ? 'page' : undefined"
          >
            <div class="flex items-center gap-1">
              <component
                v-if="item.icon"
                :is="item.icon"
                :size="iconSize"
                class="flex-shrink-0"
              />
              {{ item.label }}
            </div>
          </component>
          
          <button
            v-else-if="item.onClick"
            type="button"
            :class="[
              'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-muted rounded-sm',
              item.active 
                ? 'text-gray-900 font-medium cursor-default' 
                : 'text-gray-600 hover:text-gray-800 font-medium'
            ]"
            @click="!item.active && handleItemClick(item, index)"
            :aria-current="item.active ? 'page' : undefined"
          >
            <div class="flex items-center gap-1">
              <component
                v-if="item.icon"
                :is="item.icon"
                :size="iconSize"
                class="flex-shrink-0"
              />
              {{ item.label }}
            </div>
          </button>
          
          <span
            v-else
            :class="[
              item.active 
                ? 'text-gray-900 font-medium' 
                : 'text-gray-600 font-medium'
            ]"
            :aria-current="item.active ? 'page' : undefined"
          >
            <div class="flex items-center gap-1">
              <component
                v-if="item.icon"
                :is="item.icon"
                :size="iconSize"
                class="flex-shrink-0"
              />
              {{ item.label }}
            </div>
          </span>
        </li>
      </template>
    </ol>
  </nav>
</template>

<style scoped>
.breadcrumb-nav {
  /* Ensure proper contrast and accessibility */
  color-scheme: light;
}

.breadcrumb-nav a[aria-current="page"],
.breadcrumb-nav button[aria-current="page"] {
  pointer-events: none;
}
</style>